import { DICE_RESOLVABLE_SOURCE, WatchContextType, isChromecastSessionV1 } from '@dicetechnology/dice-doris';
import type { Http } from '@dicetechnology/dice-unity/lib/services/Http/index';
import type { IVod } from '@dicetechnology/dice-unity/lib/types/content';
import { SourceTypes } from '@dicetechnology/doris-core';
import type { LogService, Logger } from '@dicetechnology/doris-core';
import autoBind from 'auto-bind';
import type { cast as castNs } from 'chromecast-caf-receiver';
import type { QueueManager } from 'chromecast-caf-receiver/cast.framework';
import type { QueueItem } from 'chromecast-caf-receiver/cast.framework.messages';

import type { DiceCustomData } from '../../types';
import { enrichMediaMetadata, getChromecastSession, isPlaylistLoadRequest } from '../../utils';

import type { AdjacencyPlaylist, PlaylistLoadRequest, PlaylistManagerState, WatchlistLoadRequest, WatchlistManagerState } from './types';

declare const cast: typeof castNs;

const MAX_SIZE = 5;

export class PlaylistManager {
    private _currentPlaylist?: PlaylistManagerState;
    private _httpProvider: Http;
    private _isAutoAdvanceEnabled;
    private _logger: Logger;

    constructor(httpProvider: Http, loggerService: LogService) {
        autoBind(this);

        this._httpProvider = httpProvider;
        this._logger = loggerService.createLogger('PlaylistManager');
        this._isAutoAdvanceEnabled = false;
    }

    public getIsAutoAdvanceEnabled(): boolean {
        return this._isAutoAdvanceEnabled;
    }

    public setIsAutoAdvanceEnabled(enabled: boolean): void {
        if (this._isAutoAdvanceEnabled === enabled) {
            this._logger.info(`Auto advance is already ${enabled ? 'enabled' : 'disabled'}`);
            return;
        }

        this._isAutoAdvanceEnabled = enabled;

        const manager = this.getQueueManager();
        const currentItem = manager?.getCurrentItem();
        const contentId = currentItem?.media?.contentId;
        if (!manager || !currentItem || !contentId) {
            return;
        }

        if (!enabled) {
            const itemsToDelete = manager.getItems().reduce<number[]>((items, item) => {
                if (item.itemId != null && item.itemId !== currentItem?.itemId) {
                    items.push(item.itemId);
                }
                return items;
            }, [] as number[]);
            manager.removeItems(itemsToDelete);
        } else {
            if (this._currentPlaylist) {
                const payload: Partial<PlaylistLoadRequest> = {
                    id: this._currentPlaylist.id,
                    type: this._currentPlaylist.type,
                    startingContentId: contentId
                };

                if (this._currentPlaylist.type === WatchContextType.WATCHLIST) {
                    (payload as WatchlistLoadRequest).ownerExternalId = this._currentPlaylist.ownerExternalId;
                }

                if (isPlaylistLoadRequest(payload)) {
                    // Satisfy TS - we should always reach here if we have a current playlist
                    this.updatePlaylistQueue(payload);
                }
            } else {
                // Default to Seasons if we don't have a WatchContext
                this.updatePlaylistQueue({
                    id: 'unknown', // We don't know this value + its not required for Seasons
                    type: WatchContextType.SEASON,
                    startingContentId: contentId
                });
            }
        }
    }

    public async updatePlaylistQueue(loadRequest: PlaylistLoadRequest): Promise<void> {
        // If you're calling this then your intention is to enable auto queueing
        this._isAutoAdvanceEnabled = true;

        if (
            // We are fetching the same playlist and we have a buffer of items
            this._currentPlaylist?.id === loadRequest.id &&
            this._currentPlaylist.type === loadRequest.type &&
            !this.getCanFetchNextItems(this._currentPlaylist)
        ) {
            this._logger.debug('We have items in the buffer or have fetched everything, will not update queue.');
            return;
        }

        switch (loadRequest.type) {
            case WatchContextType.PLAYLIST: {
                const data = await this.getPlaylistAdjacentTo(loadRequest.id, loadRequest.startingContentId);
                await this.insertItemsToQueue(loadRequest, data);
                break;
            }
            case WatchContextType.SEASON: {
                const data = await this.getSeasonAdjacentTo(loadRequest.startingContentId);
                await this.insertItemsToQueue(loadRequest, data);
                break;
            }
            case WatchContextType.WATCHLIST: {
                const data = await this.getWatchlistAdjacentTo(loadRequest.id, loadRequest.startingContentId, loadRequest.ownerExternalId);
                await this.insertItemsToQueue(loadRequest, data);
                break;
            }
        }
    }

    private async addFollowingVods(followingVods: IVod[]): Promise<void> {
        const playerQueue = this.getQueueManager();

        if (!playerQueue) {
            return;
        }

        const currentItems = playerQueue
            .getItems()
            .map((item) => item.media?.contentId)
            .filter((id) => typeof id === 'string');

        const newFollowingVods = followingVods.filter((vod) => !currentItems.includes(vod.id.toString()));
        const result: QueueItem[] = [];

        for (const vod of newFollowingVods) {
            const queueItem = this.createQueueItem(vod);
            if (queueItem != null) {
                result.push(queueItem);
            }
        }

        if (result.length) {
            playerQueue.insertItems(result);
        }
    }

    private async addPrecedingVods(precedingVods: IVod[]): Promise<void> {
        const playerQueue = this.getQueueManager();

        if (!playerQueue) {
            return;
        }

        const currentItems = playerQueue
            .getItems()
            .map((item) => item.media?.contentId)
            .filter((id) => typeof id === 'string');

        const newPrecedingVods = precedingVods.filter((vod) => !currentItems.includes(vod.id.toString()));
        const result: QueueItem[] = [];

        for (const vod of newPrecedingVods) {
            const queueItem = this.createQueueItem(vod);
            if (queueItem != null) {
                result.push(queueItem);
            }
        }

        if (result.length) {
            playerQueue.insertItems(result, playerQueue.getItems()[0].itemId);
        }
    }

    private createQueueItem(vod: IVod): QueueItem | undefined {
        const currentItem = this.getQueueManager()?.getCurrentItem();
        if (currentItem?.media == null) {
            return;
        }

        const mediaCustomData: DiceCustomData | undefined = currentItem.media.customData;
        if (
            !mediaCustomData ||
            (mediaCustomData?.chromecastSessionSerialized == null && mediaCustomData?.chromecastSession == null) ||
            mediaCustomData.resolutionCustomData == null ||
            mediaCustomData.resolutionType !== DICE_RESOLVABLE_SOURCE ||
            mediaCustomData.sourceType !== SourceTypes.RESOLVABLE
        ) {
            return;
        }

        const queueItem = new cast.framework.messages.QueueItem();
        queueItem.autoplay = true;
        queueItem.media = {
            contentId: vod.id.toString(),
            contentType: '',
            customData: {
                activeAudioTrackLang: mediaCustomData.activeAudioTrackLang,
                // TODO:
                // Initial track selection is cloned when the element is loaded,
                // if the user modifies the track selection during playback
                // it will be reset when the next item is loaded.
                initialAudioTrack: mediaCustomData.initialAudioTrack,
                resolutionCustomData: {
                    isLive: false
                },
                resolutionType: DICE_RESOLVABLE_SOURCE,
                sourceType: SourceTypes.RESOLVABLE
            } as DiceCustomData,
            duration: vod.duration,
            streamType: cast.framework.messages.StreamType.BUFFERED,
            tracks: []
        };

        enrichMediaMetadata({ media: queueItem.media, streamInfoOrStreamData: { metadata: vod } });

        const chromecastSession = getChromecastSession(mediaCustomData);

        if (isChromecastSessionV1(chromecastSession)) {
            chromecastSession.beacon.video = vod.id;
        }

        if (mediaCustomData.chromecastSession) {
            queueItem.media.customData.chromecastSession = chromecastSession;
        }
        if (mediaCustomData.chromecastSessionSerialized) {
            queueItem.media.customData.chromecastSessionSerialized = JSON.stringify(chromecastSession);
        }

        const watchContext = mediaCustomData.watchContext;
        if (watchContext) {
            queueItem.media.customData.watchContext = watchContext;
        }

        return queueItem;
    }

    private getCanFetchNextItems(currentPlaylist: PlaylistManagerState): boolean {
        const queueManager = this.getQueueManager();

        if (!queueManager) {
            return false;
        }

        const currentIndex = queueManager.getCurrentItemIndex();
        const items = queueManager.getItems();

        if (
            (currentIndex <= 2 && // we are close to the left edge of the queue
                currentPlaylist.hasPrevious) || // we still have content to prepend
            (currentIndex >= items.length - 3 && // we are close to the edge of the queue
                currentPlaylist.hasNext) // we still have content to append)
        ) {
            return true;
        }

        return false;
    }

    private async getPlaylistAdjacentTo(playlistId: string, contentId: string): Promise<AdjacencyPlaylist> {
        const apiUrl = this._httpProvider.baseUrl.replace('/v1', '/v2');
        return this._httpProvider.get<AdjacencyPlaylist>(`${apiUrl}/playlist/${playlistId}/adjacentTo/${contentId}?size=${MAX_SIZE}`);
    }

    private getQueueManager(): QueueManager | undefined {
        const context = cast.framework.CastReceiverContext.getInstance();
        const playerManager = context.getPlayerManager();
        const queueManager = playerManager.getQueueManager();
        if (queueManager == null) {
            return;
        }

        return queueManager;
    }

    private async getSeasonAdjacentTo(contentId: string): Promise<AdjacencyPlaylist> {
        const apiUrl = this._httpProvider.baseUrl.replace('/v1', '/v4');
        return this._httpProvider.get<AdjacencyPlaylist>(`${apiUrl}/vod/${contentId}/adjacent?size=${MAX_SIZE}`);
    }

    private async getWatchlistAdjacentTo(playlistId: string, contentId: string, ownerExternalId: string): Promise<AdjacencyPlaylist> {
        const apiUrl = this._httpProvider.baseUrl.replace('/v1', '/v3');
        return this._httpProvider.get<AdjacencyPlaylist>(
            `${apiUrl}/user/watchlist/${ownerExternalId}/${playlistId}/adjacentTo/${contentId}?size=${MAX_SIZE}`
        );
    }

    private async insertItemsToQueue(loadRequest: PlaylistLoadRequest, response: AdjacencyPlaylist): Promise<void> {
        const currentPlaylist: Partial<PlaylistManagerState> = {
            hasNext: true,
            hasPrevious: true,
            id: loadRequest.id,
            type: loadRequest.type
        };

        if (loadRequest.type === WatchContextType.WATCHLIST) {
            (currentPlaylist as WatchlistManagerState).ownerExternalId = loadRequest.ownerExternalId;
        }

        if (response.parsedData.followingVods.length < MAX_SIZE) {
            currentPlaylist.hasNext = false;
        }

        if (response.parsedData.precedingVods.length < MAX_SIZE) {
            currentPlaylist.hasPrevious = false;
        }

        this._currentPlaylist = currentPlaylist as PlaylistManagerState;

        await Promise.all([
            this.addFollowingVods(response.parsedData.followingVods),
            this.addPrecedingVods(response.parsedData.precedingVods)
        ]);
    }
}
